<template>
  <div class="boss-layout boss-layout-vertical boss-layout-fill">
    <template v-if="displayKey === 'scouted'">
      <scout-members-scouted-filter
        ref="filter"
        :member-objects="memberObjects"
        @setMemberObjects="clearCheckedAndSetMemberObjects"
      />
    </template>
    <template v-else>
      <scout-members-filter
        ref="filter"
        :member-objects="memberObjects"
        :is-candidate="displayKey === 'candidate'"
        :is-search="displayKey === 'search'"
        :is-recommended="displayKey === 'recommended'"
        :job-id="jobId"
        @setMemberObjects="clearCheckedAndSetMemberObjects"
      />
    </template>
    <div class="pega-sticky">
      <div class="boss-batch-container clearfix padding-top-0">
        <members-selector
          :display-count="displayCount"
          :count-from="countFrom"
          :count-to="countTo"
          :is-paging="count > countLimit"
          :show-selector="!isEmptyBulkRegister"
          @selectedMembers="selectedMembers"
        />
        <scout-members-bulk-register
          ref="scoutMembersBulkRegister"
          :job-id="jobId"
          :contract-id="contractId"
          :member-objects="checkedMemberObjects"
          :search-name="searchName"
          :search-condition-id="searchId"
          @fetch="$emit('fetch')"
          @clearCheckedMembers="clearCheckedMembers"
        />
      </div>
    </div>
    <div>
      <div class="boss-layout boss-layout-gap-between boss-layout-center">
        <div>
          <router-link
            v-if="displayKey !== 'scouted'"
            :to="{ name: 'settingMemberAttributes' }"
            class="boss-button boss-button-compact"
          >
            表示情報を編集
          </router-link>
        </div>
        <sort-pull-down
          :objects="renderingMemberObjects"
          :sort-item-map="sortItemMap"
          @setObjects="setMemberObjects"
        />
      </div>
    </div>
    <div>
      <template v-if="count > countLimit">
        <paginate
          v-model="currentPage"
          :page-count="pageCount"
          :page-range="2"
          :click-handler="fetchPages"
          :prev-text="'＜'"
          :next-text="'＞'"
          :active-class="'current'"
          :container-class="'pagination'"
          :page-class="'page-item'"
          :disabled-class="'disabled'"
        />
      </template>
      <div class="boss-resumecard-list">
        <template v-for="(memberObject, i) in infinityMemberObjects">
          <scout-resume-card
            v-if="showResumeCard(memberObject.member.isScoutExcluded)"
            :key="i"
            :member-object="memberObject"
            :checked="checkedMemberIds"
            :is-publish-job="displayKey !== 'excluded' && isPublishJob"
            :contract-id="contractId"
            :is-scouted="displayKey === 'scouted'"
            :is-excluded="displayKey === 'excluded'"
            :is-candidate="displayKey === 'candidate'"
            :job-id="jobId"
            :searched-condition-name="searchName"
            :searched-condition-id="searchId"
            :shows-scout-button="showsScoutButton"
            :shows-check-control="!isEmptyBulkRegister"
            :needs-check-control="needsCheckControl"
            @checked="checked"
            @change="changed"
            @fetch="$emit('fetch')"
            @spliceCheckedMember="spliceCheckedMember"
          />
        </template>
      </div>
    </div>
    <infinity-scroll
      :objects="renderingMemberObjects"
      :infinity-objects.sync="infinityMemberObjects"
      :display-count="displayCount"
      :no-results-label="noResultsLabel"
    />
    <scout-recommended-footer :show-recommended-less="showRecommendedLess" />
    <selected-counter :checked-count="checkedMemberIds.length" />
  </div>
</template>

<script>
import ScoutResumeCard from '@/components/organisms/ScoutResumeCard'
import ScoutMembersBulkRegister from '@/components/molecules/ScoutMembersBulkRegister'
import MembersSelector from '@/components/molecules/MembersSelector'
import SelectedCounter from '@/components/molecules/SelectedCounter'
import SortPullDown from '@/components/atoms/SortPullDown'
import ScoutMembersFilter from '@/components/molecules/ScoutMembersFilter'
import ScoutMembersScoutedFilter from '@/components/molecules/ScoutMembersScoutedFilter'
import ScoutRecommendedFooter from '@/components/atoms/ScoutRecommendedFooter'
import InfinityScroll from '@/components/atoms/InfinityScroll'
import _ from 'lodash'
import { scoutResultSort, scoutResultSortScouted, scoutRecommendedSort, scoutResultSortExcluded } from '@/lib/labelValueMappings'
import { mapGetters } from 'vuex'
import Paginate from 'vuejs-paginate'
import Indicator from '@/lib/indicator'

export default {
  components: {
    ScoutResumeCard,
    ScoutMembersBulkRegister,
    MembersSelector,
    SelectedCounter,
    SortPullDown,
    ScoutMembersFilter,
    ScoutMembersScoutedFilter,
    ScoutRecommendedFooter,
    InfinityScroll,
    Paginate
  },
  props: {
    memberObjects: {
      type: Array,
      default: () => ([])
    },
    searchName: {
      type: String,
      default: ''
    },
    searchId: {
      type: Number,
      default: null
    },
    contractId: {
      type: Number,
      default: null
    },
    jobId: {
      type: Number,
      default: null
    },
    displayKey: {
      type: String,
      required: true
    },
    count: {
      type: Number,
      default: 0
    },
    countLimit: {
      type: Number,
      default: 0
    },
    doSearch: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      checkedMemberIds: [],
      renderingMemberObjects: this.memberObjects,
      infinityMemberObjects: [],
      currentPage: 1,
      displayCount: null,
      isEmptyBulkRegister: false
    }
  },
  computed: {
    checkedMemberObjects () {
      return this.checkedMemberIds.map(id => _(this.renderingMemberObjects).find({ id: id }))
    },
    sortItemMap () {
      if (this.displayKey === 'scouted') {
        return scoutResultSortScouted
      } else if (this.displayKey === 'recommended') {
        return scoutRecommendedSort
      } else if (this.displayKey === 'excluded') {
        return scoutResultSortExcluded
      } else {
        return scoutResultSort
      }
    },
    showsScoutButton () {
      // スカウト送信権限なし
      if (!this.isSendScout) {
        return false
      } else if (this.displayKey === 'scouted') {
        // スカウト済み一覧なら表示
        return true
      }
      return this.displayKey !== 'excluded' && this.isPublishJob
    },
    noResultsLabel () {
      if (!Indicator.getLoadingStatus()) {
        if (this.displayKey === 'scouted') {
          return 'スカウト済み会員がいません。'
        }
        if (this.displayKey === 'candidate') {
          return '送信候補がいません。'
        }
        if (this.displayKey === 'search') {
          return this.doSearch ? '検索結果がありません。' : '表示したい検索条件名を、選択してください'
        }
        if (this.displayKey === 'excluded') {
          return '除外者がいません。'
        }
        if (this.displayKey === 'recommended') {
          return 'おすすめ会員がいません。'
        }
      }
      return ''
    },
    ...mapGetters('contracts', [
      'isPublishedBy',
      'contracts',
      'canScoutResend'
    ]),
    ...mapGetters([
      'isSendScout'
    ]),
    showRecommendedLess () {
      return this.memberObjects.length <= 20 && this.displayKey === 'recommended' && !Indicator.getLoadingStatus()
    },
    pageCount () {
      return _.ceil(_.divide(this.count, this.countLimit))
    },
    countFrom () {
      return this.displayCount ? (this.currentPage - 1) * this.countLimit + 1 : null
    },
    countTo () {
      return this.displayCount ? (this.currentPage - 1) * this.countLimit + this.displayCount : null
    },
    needsCheckControl () {
      return this.displayKey === 'scouted' || this.displayKey === 'candidate' || this.displayKey === 'search' || this.displayKey === 'recommended'
    },
    isPublishJob () {
      return this.isPublishedBy(this.contractId, this.jobId)
    },
    shouldExcludedFilter () {
      return _.includes(['search', 'candidate', 'recommended'], this.displayKey)
    }
  },
  watch: {
    memberObjects (val) {
      this.setMemberObjects(val)
    },
    renderingMemberObjects: {
      handler () {
        this.setDisplayCount()
      },
      deep: true
    },
    jobId () {
      // 求人が変わった初期ページ
      this.$set(this, 'currentPage', 1)
      // 絞り込み初期化
      this.$refs.filter.clearFilterData()
    },
    count () {
      this.$set(this, 'currentPage', 1)
    },
    currentPage () {
      this.$set(this, 'displayCount', null)
    },
    doSearch () {
      this.doSearch && this.$set(this, 'displayCount', null)
    },
    shouldExcludedFilter () {
      this.setDisplayCount()
    }
  },
  created () {
    if (this.displayKey === 'search') {
      this.$set(this, 'displayCount', 0)
    }
  },
  mounted () {
    this.setIsEmptyBulkRegister()
  },
  methods: {
    checked (id) {
      if (!_.includes(this.checkedMemberIds, id)) {
        this.checkedMemberIds.push(id)
      }
    },
    changed (val) {
      if (_.includes(this.checkedMemberIds, val)) {
        this.checkedMemberIds.splice(this.checkedMemberIds.findIndex(n => n === val), 1)
      } else {
        this.checkedMemberIds.push(val)
      }
    },
    clearCheckedMembers () {
      this.$set(this, 'checkedMemberIds', [])
    },
    selectedMembers (selectLength, isChecked) {
      const memberObjects = this.needsCheckControl ? this.filterBulkChecked(this.renderingMemberObjects, isChecked) : this.renderingMemberObjects
      memberObjects.slice(0, selectLength).forEach((memberObject) => {
        if (isChecked) {
          if (!_.includes(this.checkedMemberIds, memberObject.id)) {
            this.checkedMemberIds.push(memberObject.id)
          }
        } else {
          this.spliceCheckedMember(memberObject.id)
        }
      })
    },
    setMemberObjects (memberObjects) {
      this.$set(this, 'renderingMemberObjects', memberObjects)
    },
    setDisplayCount () {
      const count = this.shouldExcludedFilter
        ? this.renderingMemberObjects.filter(memberObject => !memberObject.member.isScoutExcluded).length
        : this.renderingMemberObjects.length
      this.$set(this, 'displayCount', count)
    },
    clearCheckedAndSetMemberObjects (memberObjects) {
      this.clearCheckedMembers()
      this.setMemberObjects(memberObjects)
    },
    fetchPages (pageNum) {
      this.$emit('fetch', pageNum)
    },
    filterBulkChecked (memberObjects, isChecked) {
      const filterMemberObjects = memberObjects.filter(memberObject => {
        return (!_.get(memberObject, 'member.isScoutExcluded') &&
          !_.get(memberObject, 'member.isEntry') &&
          !_.get(memberObject, 'member.isScoutRejection') &&
          !_.get(memberObject, 'member.withdrawDate') &&
          _.get(memberObject, 'member.subscriptions.scout') &&
          !_.get(memberObject, 'member.isScoutReserved') &&
          !(_.get(memberObject, 'member.isScoutDecline') && this.canScoutResend(this.contractId, this.jobId)))
      })
      isChecked && this.$toasted.global.success({ message: 'スカウト送信可能な会員のみ選択しました。' })
      return filterMemberObjects
    },
    spliceCheckedMember (memberObjectId) {
      this.checkedMemberIds.splice(this.checkedMemberIds.findIndex(id => id === memberObjectId), 1)
    },
    showResumeCard (isScoutExcluded) {
      return this.shouldExcludedFilter ? !isScoutExcluded : true
    },
    setIsEmptyBulkRegister () {
      this.$set(this, 'isEmptyBulkRegister', !this.getBulkRegisterOptionsCount())
    },
    getBulkRegisterOptionsCount () {
      return this.$refs.scoutMembersBulkRegister.bulkRegisterOptions.length
    }
  }
}
</script>
